import { lightTap } from "@/utils/haptics.js";

let sharedAudioContext = null;

export const ALERT_TONE_PROFILES = {
  soft: {
    pulses: [
      { frequency: 720, duration: 0.13, gain: 0.06, type: "sine" },
      { frequency: 880, duration: 0.12, gain: 0.052, type: "sine", delay: 0.06 },
    ],
  },
  standard: {
    pulses: [
      { frequency: 880, duration: 0.16, gain: 0.1, type: "triangle" },
      { frequency: 1175, duration: 0.15, gain: 0.088, type: "triangle", delay: 0.07 },
    ],
  },
  urgent: {
    pulses: [
      { frequency: 920, duration: 0.16, gain: 0.12, type: "triangle" },
      { frequency: 1280, duration: 0.15, gain: 0.108, type: "triangle", delay: 0.06 },
      { frequency: 980, duration: 0.14, gain: 0.096, type: "triangle", delay: 0.06 },
    ],
  },
};

export const DEFAULT_MESSAGE_ALERT_TONE = "standard";

function getAudioContext() {
  if (typeof window === "undefined") return null;
  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  if (!AudioCtx) return null;
  if (!sharedAudioContext || sharedAudioContext.state === "closed") {
    sharedAudioContext = new AudioCtx();
  }
  return sharedAudioContext;
}

export async function primeUrgentAlertTone() {
  const ctx = getAudioContext();
  if (!ctx) return false;

  try {
    if (ctx.state === "suspended") {
      await ctx.resume();
    }
    return ctx.state === "running";
  } catch {
    return false;
  }
}

export function canShowBrowserNotification() {
  return typeof window !== "undefined" && "Notification" in window;
}

export function showBrowserDeviceAlert({
  title,
  body,
  tag,
  route,
  icon = "/icons/appointments/dashboard/notifications.svg",
  onNavigate,
} = {}) {
  if (!canShowBrowserNotification()) return null;
  if (window.Notification.permission !== "granted") return null;

  try {
    const notification = new window.Notification(String(title || "AfriMed"), {
      body: String(body || "").trim(),
      tag: String(tag || "").trim() || undefined,
      icon,
      badge: icon,
      silent: true,
    });

    notification.onclick = () => {
      try {
        window.focus?.();
      } catch {
        // ignore focus failures
      }
      onNavigate?.(route);
      notification.close();
    };

    return notification;
  } catch {
    return null;
  }
}

export async function playUrgentAlertTone(profile = DEFAULT_MESSAGE_ALERT_TONE) {
  const ctx = getAudioContext();
  if (!ctx) return false;

  try {
    if (ctx.state === "suspended") {
      const primed = await primeUrgentAlertTone();
      if (!primed) return false;
    }

    const toneProfile =
      ALERT_TONE_PROFILES[String(profile || "").trim().toLowerCase()] ||
      ALERT_TONE_PROFILES[DEFAULT_MESSAGE_ALERT_TONE] ||
      ALERT_TONE_PROFILES.standard;
    const now = ctx.currentTime;

    toneProfile.pulses.forEach((pulse, index) => {
      const startAt =
        now +
        toneProfile.pulses
          .slice(0, index)
          .reduce((total, item) => total + Number(item.duration || 0) + Number(item.delay || 0), 0);
      const duration = Number(pulse.duration || 0.12);
      const peakGain = Math.min(Number(pulse.gain || 0.08), 0.14);
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();

      oscillator.type = pulse.type || "triangle";
      oscillator.frequency.setValueAtTime(Number(pulse.frequency || 880), startAt);
      oscillator.frequency.exponentialRampToValueAtTime(
        Math.max(220, Number(pulse.frequency || 880) * 0.92),
        startAt + duration
      );

      gainNode.gain.setValueAtTime(0.0001, startAt);
      gainNode.gain.exponentialRampToValueAtTime(peakGain, startAt + 0.018);
      gainNode.gain.exponentialRampToValueAtTime(0.0001, startAt + duration);

      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      oscillator.start(startAt);
      oscillator.stop(startAt + duration + 0.01);
    });

    return true;
  } catch {
    return false;
  }
}

export function triggerUrgentHaptic() {
  return lightTap([30, 45, 30, 45, 42]);
}

export async function runMessageAlertPreview({
  soundProfile = DEFAULT_MESSAGE_ALERT_TONE,
  vibrationOn = false,
} = {}) {
  const normalizedProfile = String(soundProfile || "").trim().toLowerCase();
  const soundPlayed =
    normalizedProfile === "off" ? false : await playUrgentAlertTone(normalizedProfile);
  const vibrationTriggered = vibrationOn ? triggerUrgentHaptic() : false;

  return {
    soundPlayed,
    vibrationTriggered,
  };
}
